import React, { useEffect } from "react";
import { Form, Input, InputNumber, Select } from "antd";
import FormItem from "components/fields/FormItem";
import { connect } from "react-redux";
import {
  getRewardDetails,
  doRewardCreate,
  doRewardUpdate,
} from "../../../../redux/reward/rewardState";
import CrudButtons from "components/button/crudbuttons";
import { requiredField } from "utils/crudSagaHelper";
import { USABLE_ICONS, COLOR_OPTIONS } from "../../../../variables/constants"; 
import SwitchField from "components/fields/SwitchField";

const RewardForm = ({
  recordData,
  getRecord,
  doCreate,
  doUpdate,
  onCancel,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const { recordId, mode } = props;

  const isAutomatic = Form.useWatch("rwd_is_automatic", form) ?? recordData?.rwd_is_automatic;

  useEffect(() => {
    if (recordId) getRecord(recordId);
  }, [recordId, getRecord]);

  useEffect(() => {
    if (recordData) {
      form.resetFields();
      const initialValues = {
        rwd_title: recordData?.rwd_title ?? "",
        rwd_description: recordData?.rwd_description ?? "",
        rwd_icon: recordData?.rwd_icon ?? "",
        rwd_color_class: recordData?.rwd_color_class ?? "",
        rwd_points: recordData?.rwd_points ?? 0,
        rwd_is_automatic: recordData?.rwd_is_automatic ?? 0,
        rwd_trigger_type_id: recordData?.rwd_trigger_type_id ?? 0,
      };
      form.setFieldsValue(initialValues);
    }
  }, [recordData, form]);

  const onSubmit = (formData) => {

    try {
      formData.rwd_is_automatic = formData.rwd_is_automatic ? 1 : 0;
        
      recordData?.rwd_id > 0 ? doUpdate(formData) : doCreate(formData);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      className={mode !== "view" ? "form" : ""}
    >
      <FormItem name="rwd_id" hidden>
        <Input type="hidden" />
      </FormItem>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormItem
          name="rwd_title"
          label="Award Title"
          rules={[requiredField]}
          className="col-span-2 md:col-span-1"
        >
          {mode !== "view" ? (
            <Input
              id="rwd_title"
              placeholder="Enter award title"
              className="input-field"
              maxLength={100}
            />
          ) : (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_title}
            </label>
          )}
        </FormItem>

        <FormItem
          name="rwd_description"
          label="Description"
          rules={[requiredField]}
          className="col-span-2"
        >
          {mode !== "view" ? (
            <Input
              placeholder="Enter description"
              className="input-field"
              maxLength={300}
            />
          ) : (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_description}
            </label>
          )}
        </FormItem>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormItem
          name="rwd_icon"
          label="Icon"
          rules={[requiredField]}
        >
          {mode !== "view" ? (
            <Select
              placeholder="Select icon"
              className="input-field"
              showSearch
              allowClear
              options={Object.keys(USABLE_ICONS).map((key) => ({
                label: key,
                value: key,
              }))}
            />
          ) : (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_icon}
            </label>
          )}
        </FormItem>

        <FormItem
          name="rwd_color_class"
          label="Color"
          rules={[requiredField]}
        >
          {mode !== "view" ? (
            <Select
              placeholder="Select color"
              className="input-field"
              options={COLOR_OPTIONS}
            />
          ) : (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_color_class}
            </label>
          )}
        </FormItem>

        <FormItem
          name="rwd_points"
          label="Points"
          rules={[requiredField]}
        >
          {mode !== "view" ? (
            <InputNumber
              placeholder="Set points"
              className="input-field"
            />
          ) : (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_points}
            </label>
          )}
        </FormItem>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormItem 
          label="Is Automatic"
          name="rwd_is_automatic"
        >
          {mode !== "view" ?
            <SwitchField
              id="rwd_is_automatic"
              mode="edit"
              type="text"
              labelYes="Yes"
              labelNo="No"
            />
            : 
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_is_automatic}
            </label>
          }
        </FormItem>
        <FormItem
          name="rwd_trigger_type_id"
          label="Trigger Type"
          rules={isAutomatic && mode !== "view" ? [requiredField] : []}
          className="col-span-2 md:col-span-1"
        >
          {mode === "view" ? (
            <label className="text-base label-value dark:text-white">
              {recordData?.rwd_trigger_type_id || "-"}
            </label>
          ) : (
            <Select
              placeholder="Select trigger type"
              className="input-field"
              options={recordData?.rwdTriggerTypes}
              disabled={!isAutomatic}
            />
          )}
        </FormItem>
      </div>

      <div className="w-full flex justify-end">
        {(mode === "edit") && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.rwd_id ?? 0}
            isModal={true}
            modalId={recordData?.rwd_id > 0 ? "edit_reward" : "add_reward"}
            cancelLabel="Cancel"
            viewMode="modal"
            doHideModal={onCancel}
            submitLabel={submitLabel}
          />
        )}
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  recordData: state.reward.rewardDetails?.data, 
});

const mapDispatchToProps = (dispatch) => ({
  getRecord: (id) => dispatch(getRewardDetails(id)),
  doCreate: (data) => dispatch(doRewardCreate(data)),
  doUpdate: (data) => dispatch(doRewardUpdate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RewardForm);
